// 📚 References → Hospital References — ask the hospital's approved clinical
// references a question (G1, docs/HMS_V4_CLINICAL_RAG_DESIGN.md §23). Asked
// from Consultation (with the visit) or an admission (with the admission), the
// answer takes the patient's age, allergies, diagnoses and medicines into
// account, and their name and identifiers are never sent. Nothing here writes
// to a prescription or an order; "Insert into note" only adds text to the open
// note draft.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import VoiceMicButton from '@/components/VoiceMicButton';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { askReferences, fetchReferenceHistory, fetchReferenceQuery, fetchReferenceStatus, type ReferenceAnswer } from './api';
import { ReferenceAnswerView } from './ReferenceAnswerView';

export function HospitalReferencesPanel({
  active, visitId = null, admissionId = null, patientLabel, onInsert,
}: {
  /** The drawer is open and this tab is showing. */
  active: boolean;
  visitId?: number | null;
  admissionId?: number | null;
  patientLabel?: string | null;
  onInsert?: (text: string) => void;
}) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<ReferenceAnswer | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const status = useQuery({ queryKey: keys.clinical.referenceStatus(), queryFn: fetchReferenceStatus, enabled: active, staleTime: 60_000 });
  const history = useQuery({ queryKey: keys.clinical.referenceHistory(), queryFn: fetchReferenceHistory, enabled: active && showHistory });
  const ask = useMutation({
    mutationFn: (q: string) => askReferences({ question: q, visit_id: visitId, admission_id: admissionId }),
    onSuccess: (a) => { setAnswer(a); qc.invalidateQueries({ queryKey: keys.clinical.referenceHistory() }); },
  });
  const reopen = useMutation({ mutationFn: fetchReferenceQuery, onSuccess: (a) => { setAnswer(a); setShowHistory(false); } });

  // A new patient is a new conversation.
  useEffect(() => { setAnswer(null); ask.reset(); }, [visitId, admissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => { const q = question.trim(); if (q.length >= 3 && !ask.isPending) ask.mutate(q); };
  const error = ask.error instanceof ApiError || ask.error instanceof Error ? ask.error.message : null;
  const noDocs = status.data && status.data.approved_documents === 0;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-ink-muted">
        Answers come only from documents your hospital has approved, with the source of every statement.
        {patientLabel ? ` Answered for ${patientLabel}; their name and identifiers are not sent.` : ''}
      </p>
      {noDocs && (
        <div className="rounded-lg bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
          No reference documents have been approved yet. An administrator can add them in Admin → Clinical → Reference Library.
        </div>
      )}
      <div className="flex flex-col gap-2">
        <Textarea
          value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} maxLength={1000}
          placeholder="e.g. First-line antibiotic for UTI with penicillin allergy?"
          aria-label="Your question for the hospital references"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        />
        <div className="flex items-center gap-2">
          <VoiceMicButton translateToEnglish={false} title="Speak your question" onText={(t) => setQuestion((q) => (q ? `${q} ${t}` : t))} />
          <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}><History className="mr-1 h-3.5 w-3.5" /> Earlier questions</Button>
          <Button className="ml-auto" size="sm" loading={ask.isPending} disabled={question.trim().length < 3} onClick={submit}>
            <Send className="mr-1 h-3.5 w-3.5" /> Ask
          </Button>
        </div>
        {ask.isPending && <div className="text-[12px] text-ink-muted">Searching the approved references…</div>}
        {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
      </div>

      {showHistory && (
        <div className="rounded-lg border border-border">
          {history.isLoading && <div className="px-3 py-2 text-[12px] text-ink-muted">Loading…</div>}
          {history.data?.length === 0 && <div className="px-3 py-2 text-[12px] text-ink-muted">No earlier questions.</div>}
          <ul className="max-h-56 divide-y divide-border overflow-auto">
            {history.data?.map((h) => (
              <li key={h.id}>
                <button type="button" className="w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-muted" onClick={() => reopen.mutate(h.id)}>
                  <span className="block truncate text-ink">{h.question}</span>
                  <span className="text-[11px] text-ink-muted">
                    {new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {h.insufficient_evidence ? ' · not covered' : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {answer && (
        <div className="flex flex-col gap-2">
          <div className="text-[12px] text-ink-muted">You asked: <span className="text-ink">{answer.question}</span></div>
          <ReferenceAnswerView answer={answer} onInsert={onInsert} />
        </div>
      )}
    </div>
  );
}
